import { useState, useEffect } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faSquarePlus, faSquareMinus } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import EditModal from "../ui/EditModal.js";
import { normalizar } from "../../common/stringNormalizer.js";

const AddMedicaciones = () => {
  // Leer el filtro guardado en localStorage o usar "activas" por defecto
  const [filtroEstado, setFiltroEstado] = useState(() => {
    return localStorage.getItem("filtroMedicaciones") || "activas";
  });
  const [Nombre, setNombre] = useState("");
  const [Descripcion, setDescripcion] = useState("");
  const [Medicaciones, setMedicaciones] = useState([]);
  const [mostrarDescripcion, setMostrarDescripcion] = useState({});
  const [selectedMedicacion, setSelectedMedicacion] = useState(null);

  useEffect(() => {
    getMedicaciones();
  }, []);

  // Guardar el filtro en localStorage cada vez que cambie
  useEffect(() => {
    localStorage.setItem("filtroMedicaciones", filtroEstado);
  }, [filtroEstado]);

  const editFields = [

    { name: "Nombre", label: "Nombre Medicación", type: "text", required: true },
    { name: "Descripcion", label: "Descripción Medicación", type: "text", required: false }
  ];

  const getMedicaciones = async () => {
    try {
      const response = await api.get(`/medicaciones`);
      setMedicaciones(response.data);
    } catch (error) {
      console.error("Error al obtener medicaciones:", error);
    }
  };

  const createMedicacion = async (e) => {
    e.preventDefault();

    const existe = Medicaciones.some(
      (m) => normalizar(m.Nombre) === normalizar(Nombre)
    );

    if (existe) {
      Swal.fire({
        icon: "warning",
        title: "Duplicado",
        text: "Ya existe una medicación con ese nombre.",
        confirmButtonColor: "#3085d6"
      });
      return;
    }

    try {
      // Crear siempre con Estado = 1 (activa)
      await api.post(`/medicaciones`, { Nombre, Descripcion });
      getMedicaciones();
      setNombre("");
      setDescripcion("");
      setFiltroEstado("activas"); // asegurar que se vea en la pestaña activas
      Swal.fire({
        icon: "success",
        title: "Medicación guardada",
        text: "La medicación fue guardada exitosamente.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: "Ocurrió un problema al guardar la medicación.",
      });
    }
  };

  const handleEditMedicacion = async (values) => {
    try {
   await api.put(`/medicaciones/${values.IdMedicacion}`, {
  Nombre: values.Nombre,
  Descripcion: values.Descripcion
      });
      Swal.fire({
        icon: "success",
        title: "Medicación actualizada",
        showConfirmButton: false,
        timer: 1500,
      });
      setSelectedMedicacion(null);
      getMedicaciones();
    } catch (error) {
      console.error("Error al actualizar:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar la medicación.",
      });
    }
  };

const cambiarEstado = async (IdMedicacion) => {
  try {
    const medicacionActual = Medicaciones.find(
      (m) => m.IdMedicacion === IdMedicacion
    );

    const nuevoEstado = medicacionActual.Estado === 1 ? 0 : 1;
    const estadoTexto = nuevoEstado === 1 ? "activada" : "desactivada";

    // 🟡 Confirmación SOLO si se va a desactivar
    if (nuevoEstado === 0) {
      const result = await Swal.fire({
        icon: "warning",
        title: "¿Desactivar medicación?",
        text: "Si está siendo utilizada, no podrá desactivarse.",
        showCancelButton: true,
        confirmButtonText: "Sí, desactivar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;
    }

    // 🔵 Llamada al backend (EL QUE MANDA)
    await api.patch(`/medicaciones/${IdMedicacion}/estado`);

    // ✅ Recién ahora actualizamos el estado local
    setMedicaciones(prev =>
      prev.map(m =>
        m.IdMedicacion === IdMedicacion
          ? { ...m, Estado: nuevoEstado }
          : m
      )
    );

    // 🔄 Cambiar automáticamente al filtro correspondiente
    setFiltroEstado(nuevoEstado === 1 ? "activas" : "inactivas");

    Swal.fire({
      icon: "success",
      title: `Medicación ${estadoTexto}`,
      text: `La medicación fue ${estadoTexto} correctamente.`,
      timer: 1500,
      showConfirmButton: false,
    });

  } catch (error) {
    console.error("Error al cambiar estado:", error);

    // 🔴 Caso especial: backend bloqueó (409)
    if (error.response?.status === 409) {
      Swal.fire({
        icon: "warning",
        title: "No permitido",
        text: error.response.data.msg,
      });
      return;
    }

    // 🔴 Error real
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Ocurrió un error al cambiar el estado de la medicación.",
    });
  }
};

  const filtrarMedicaciones = () => {
    if (filtroEstado === "activas") return Medicaciones.filter(m => m.Estado === 1);
    if (filtroEstado === "inactivas") return Medicaciones.filter(m => m.Estado === 0);
    return Medicaciones;
  };

  const columnas = [
    {
      name: "Nombre",
      selector: row => row.Nombre,
      sortable: true,
      width: "15vw",
    },
    {
      name: "Descripción",
      cell: row => {
        const toggleDescripcion = () => {
          setMostrarDescripcion({
            ...mostrarDescripcion,
            [row.IdMedicacion]: !mostrarDescripcion[row.IdMedicacion]
          });
        };

        const descripcion = row.Descripcion || "Sin descripción";
        const mostrarBoton = row.Descripcion && row.Descripcion.length > 40;

        return (
          <div style={{ display: "flex", alignItems: "center", width: "30vw", overflow: "hidden" }}>
            <div style={{ flex: 1 }}>
              {!mostrarDescripcion[row.IdMedicacion]
                ? descripcion.length > 40 ? descripcion.substring(0, 40) + "..." : descripcion
                : descripcion
              }
            </div>
            {mostrarBoton && (
              <button className="btn" onClick={toggleDescripcion}>
                {mostrarDescripcion[row.IdMedicacion] ? "Ver Menos" : "Ver Más"}
              </button>
            )}
          </div>
        );
      },
      sortable: true,
      width: "30vw",
    },
    {
      cell: row => (
        <button className="button is-small is-info" title="Editar" onClick={() => setSelectedMedicacion(row)}>
          <FontAwesomeIcon icon={faPencil} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
    {
      cell: row => row.Estado === 1
        ? <FontAwesomeIcon icon={faSquarePlus} style={{ color: "#37ff00", cursor: "pointer" }} size="2xl" onClick={() => cambiarEstado(row.IdMedicacion)} />
        : <FontAwesomeIcon icon={faSquareMinus} style={{ color: "#ff0000", cursor: "pointer" }} size="2xl" onClick={() => cambiarEstado(row.IdMedicacion)} />,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      compact: true,
    },
  ];

  const paginationOptionsES = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Todos',
  };

  return (
    <div className="columns mt-5 is-centered">
      <div>
        <div className="notification is-primary">
          <strong>Agregar Medicación</strong>
        </div>

        <form onSubmit={createMedicacion}>
          <div className="field">
            <label className="label required">Nombre Medicación</label>
            <div className="control">
              <input
                type="text"
                className="input"
                value={Nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre"
                required
              />
            </div>
          </div>

          <div className="field">
            <label className="label">Descripción Medicación</label>
            <div className="control">
              <input
                type="text"
                className="input"
                value={Descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Descripción"
              />
            </div>
          </div>

          <div className="field mt-3">
            <button type="submit" className="button is-success">Guardar</button>
          </div>
        </form>

        <div style={{ display: 'flex', gap: '10px', margin: '1rem 0' }}>
          {["todas", "activas", "inactivas"].map(estado => (
            <button
              key={estado}
              type="button"
              onClick={() => setFiltroEstado(estado)}
              className={`button is-small ${filtroEstado===estado?'is-primary':'is-light'}`}
            >
              {estado.charAt(0).toUpperCase() + estado.slice(1)}
            </button>
          ))}
        </div>

        <DataTable
          pagination
          paginationComponentOptions={paginationOptionsES}
          columns={columnas}
          data={filtrarMedicaciones()}
        />

        {selectedMedicacion && (
          <EditModal
            fields={editFields}
            initialValues={selectedMedicacion}
            onSubmit={handleEditMedicacion}
            title="Editar Medicación"
            onClose={() => setSelectedMedicacion(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AddMedicaciones;
